// Agent change history (#5208, epic #5200 G4) — who last changed an agent's model or
// its on/off switch, and a short timeline. Pure: which config keys an agent answers
// for, and the parse of one route/mode audit row into a timeline entry. The rows are
// read in `@developerz/db` (`queries/agent-history.ts`) and `GET /v1/agents/history`
// (`apps/api/src/routes/agents.ts`) is the one door.
//
// NO NEW TELEMETRY. Every entry is an `audit_events` row the route and mode controls
// already append (`llm_route.set` / `.cleared`, `agent_mode.set` / `.cleared`, stored
// as `mcp_tool.call.ok` with the event name on `tool`). #5208 widened those rows with
// `before` / `after` / `via` / `tokenId`; a row written before that simply lacks them,
// which this parse answers as `null`, never as a guess.
//
// A mode event projects ONLY `enabled` (D11): a mode's `codeTools` stay behind
// `write:prompts`, and this read is `read:billing`. The parse never reads them.

import { z } from 'zod';
import {
  type AgentModeKey,
  AgentModeKeySchema,
  type AgentModeScope,
  AgentModeScopeSchema,
  agentModeWritable,
} from '../entities/agent-mode';
import { AGENT_ROSTER, type AgentId, type AgentRoster } from './agent-roster';
import type { LlmRoutePurposeAny } from './job-class-routing';

/** How many entries a history page answers when the caller names no `per_page`. */
export const AGENT_HISTORY_DEFAULT_PER_PAGE = 5;
/** The most one history page answers; older entries page on by `cursor`. */
export const AGENT_HISTORY_MAX_PER_PAGE = 50;

/** The audit `tool` values a history reads — route pins, then mode switches. */
export const AGENT_ROUTE_EVENTS = ['llm_route.set', 'llm_route.cleared'] as const;
export const AGENT_MODE_EVENTS = ['agent_mode.set', 'agent_mode.cleared'] as const;
export const AGENT_CONFIG_EVENTS = [...AGENT_ROUTE_EVENTS, ...AGENT_MODE_EVENTS] as const;
export type AgentConfigEvent = (typeof AGENT_CONFIG_EVENTS)[number];

/** The credential kind a #5208 row records: a dashboard cookie, or anything else. */
export const CredentialViaSchema = z.enum(['session', 'pat']);
export type CredentialVia = z.infer<typeof CredentialViaSchema>;

/**
 * The config keys an agent's history covers: the routing purposes it OWNS
 * (`AGENT_ROSTER.purpose`), and the mode switch it owns (`AGENT_ROSTER.mode`). One switch,
 * one agent: CI Fixer's history is its own `ci_fixer` switch (#7390), and a flip of the
 * Coder's `worker` switch is the Coder's history alone.
 *
 * `modelFrom` is deliberately NOT followed: DevOps runs on the Coder's model, but
 * the picker that changes it is the Coder's, so those changes are the Coder's
 * history. DevOps' own history is its mode switch.
 */
export function agentConfigKeys(
  agent: AgentId,
  roster: AgentRoster = AGENT_ROSTER,
): { purposes: LlmRoutePurposeAny[]; modes: AgentModeKey[] } {
  const purposes = (Object.entries(roster.purpose) as [LlmRoutePurposeAny, AgentId][])
    .filter(([, owner]) => owner === agent)
    .map(([purpose]) => purpose);
  // A switch the doors of ANY scope can write is history the page shows (#7008, widened by
  // #6758 / #6762 / #6763): `escalation`, `triage` and `gardener` are machine-only keys, so
  // their agents' switch changes are machine-scope audit rows — the org-only filter this
  // replaced erased them from the history. A key NO scope may write yet (`planner` #6764)
  // still has no change to list.
  const modes = (Object.entries(roster.mode) as [AgentModeKey, AgentId][])
    .filter(([key]) => AgentModeScopeSchema.options.some((scope) => agentModeWritable(scope, key)))
    .filter(([, owner]) => owner === agent)
    .map(([key]) => key);
  return { purposes, modes };
}

/** A route pin as a #5208 audit row snapshots it (snake_case, like the route view). */
const PinSnapshotSchema = z.object({
  key_label: z.string().nullable(),
  provider: z.string().nullable(),
  model: z.string().nullable(),
  reasoning_effort: z.string().nullable(),
});
export type AgentHistoryPin = z.infer<typeof PinSnapshotSchema>;

/** A route pin's scope: routes have no machine scope. */
const RouteScopeSchema = z.enum(['org', 'repo']);

/** An `llm_route.*` row's `input`. The #5208 fields are optional: older rows lack them. */
const RouteInputSchema = z.object({
  scope: RouteScopeSchema,
  scopeRef: z.string(),
  purpose: z.string(),
  before: PinSnapshotSchema.nullable().optional(),
  after: PinSnapshotSchema.nullable().optional(),
  via: CredentialViaSchema.optional(),
  tokenId: z.string().optional(),
});

/**
 * An `agent_mode.*` row's `input` — `codeTools` is never read (D11). The scope is the
 * stored mode scope, `machine` included (#7008): the first machine-scope row (#6756) must
 * parse, because this parse THROWS on a row it cannot read.
 */
const ModeInputSchema = z.object({
  scope: AgentModeScopeSchema,
  scopeRef: z.string(),
  modeKey: AgentModeKeySchema,
  enabled: z.boolean().optional(),
  via: CredentialViaSchema.optional(),
  tokenId: z.string().optional(),
});

/** One `audit_events` row as the history read answers it, joined to its member. */
export type AgentHistoryAuditRow = {
  readonly id: string;
  readonly ts: Date;
  readonly tool: string;
  /** `audit_events.user_id` — the acting member, null for a user-less credential. */
  readonly userId: string | null;
  readonly input: unknown;
  /** The member's `users.login` / `users.avatar`; null when the user row is gone. */
  readonly login: string | null;
  readonly avatar: string | null;
};

/** One timeline entry. `null` is "this row does not record it", never a default. */
export type AgentHistoryEntry = {
  readonly id: string;
  readonly at: Date;
  readonly event: AgentConfigEvent;
  /** `org` / `repo` on a route event; any {@link AgentModeScope} on a mode event. */
  readonly scope: AgentModeScope;
  readonly scopeRef: string;
  /** The route purpose (route events), else null. */
  readonly purpose: string | null;
  /** The mode (mode events), else null. */
  readonly modeKey: AgentModeKey | null;
  /** The pin before / after (route events written since #5208), else null. */
  readonly before: AgentHistoryPin | null;
  readonly after: AgentHistoryPin | null;
  /** The switch a mode SET stored, else null. */
  readonly enabled: boolean | null;
  readonly via: CredentialVia | null;
  readonly actor: {
    readonly userId: string;
    readonly login: string | null;
    readonly avatarUrl: string | null;
  } | null;
  readonly tokenId: string | null;
};

function isEvent<T extends readonly string[]>(list: T, tool: string): tool is T[number] {
  return (list as readonly string[]).includes(tool);
}

/**
 * One audit row → one entry. THROWS on a row whose `tool` is not a config event or
 * whose `input` lacks the address every writer has always recorded: the read selected
 * it on exactly those fields, so a failure here is drift, and dropping it would show
 * a timeline with a silent hole.
 */
export function agentHistoryEntry(row: AgentHistoryAuditRow): AgentHistoryEntry {
  const actor =
    row.userId === null ? null : { userId: row.userId, login: row.login, avatarUrl: row.avatar };
  if (isEvent(AGENT_ROUTE_EVENTS, row.tool)) {
    const input = RouteInputSchema.parse(row.input);
    return {
      id: row.id,
      at: row.ts,
      event: row.tool,
      scope: input.scope,
      scopeRef: input.scopeRef,
      purpose: input.purpose,
      modeKey: null,
      before: input.before ?? null,
      after: input.after ?? null,
      enabled: null,
      via: input.via ?? null,
      actor,
      tokenId: input.tokenId ?? null,
    };
  }
  if (isEvent(AGENT_MODE_EVENTS, row.tool)) {
    const input = ModeInputSchema.parse(row.input);
    return {
      id: row.id,
      at: row.ts,
      event: row.tool,
      scope: input.scope,
      scopeRef: input.scopeRef,
      purpose: null,
      modeKey: input.modeKey,
      before: null,
      after: null,
      enabled: row.tool === 'agent_mode.set' ? (input.enabled ?? null) : null,
      via: input.via ?? null,
      actor,
      tokenId: input.tokenId ?? null,
    };
  }
  throw new Error(
    `agent history: audit row ${row.id} has tool ${JSON.stringify(row.tool)}, not one of ` +
      `${AGENT_CONFIG_EVENTS.join(', ')} — the read selects on those; fix the query`,
  );
}
